import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CONFIRM_PHRASE = "DELETE";

/**
 * Type-to-confirm account deletion modal. Calls `DELETE /api/me`, clears the
 * local auth session on success, then redirects to home with a toast. Works
 * for both the in-app trigger (UserMenu) and the public /delete-account page.
 *
 * Props:
 *   open, onOpenChange — controlled state from parent.
 *   onDeleted — optional callback fired AFTER local auth is cleared.
 */
export default function DeleteAccountDialog({ open, onOpenChange, onDeleted }) {
    const { token, logout } = useAuth();
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);
    const ready = confirmText === CONFIRM_PHRASE;

    const handleDelete = async () => {
        if (!ready || busy || !token) return;
        setBusy(true);
        try {
            await axios.delete(`${API}/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // Clear local auth (does not call /api/auth/me again — token is now invalid).
            logout();
            onOpenChange(false);
            toast.success("Your account has been deleted.");
            if (typeof onDeleted === "function") onDeleted();
            // Hard redirect to home so any in-flight queries cancel and the
            // app fully resets to the anonymous shell.
            setTimeout(() => {
                window.location.assign("/");
            }, 100);
        } catch (e) {
            console.warn("[delete-account] failed", e?.response?.status);
            toast.error("Could not delete account. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    // Reset the confirm field whenever the dialog opens.
    const onOpenChangeWrapped = (next) => {
        if (!next) setConfirmText("");
        onOpenChange(next);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChangeWrapped}>
            <DialogContent
                className="max-w-md border-[#E5E5E5] bg-white text-[#0A0A0A]"
                data-testid="delete-account-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-[18px] font-semibold tracking-tight">
                        Delete your Piqpath account?
                    </DialogTitle>
                    <DialogDescription className="text-[13px] leading-relaxed text-[#525252]">
                        This permanently deletes your account, all chat sessions, shared
                        links, and saved preferences. This cannot be undone.
                    </DialogDescription>
                </DialogHeader>
                <div className="mt-2 space-y-2">
                    <label
                        htmlFor="confirm-delete-input"
                        className="block text-[12px] text-[#525252]"
                    >
                        Type{" "}
                        <span className="font-mono font-semibold text-[#0A0A0A]">
                            {CONFIRM_PHRASE}
                        </span>{" "}
                        to confirm
                    </label>
                    <input
                        id="confirm-delete-input"
                        data-testid="confirm-delete-input"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[14px] text-[#0A0A0A] outline-none transition focus:border-[#0A0A0A]"
                        placeholder={CONFIRM_PHRASE}
                    />
                </div>
                <DialogFooter className="mt-4 flex gap-2 sm:gap-2">
                    <button
                        type="button"
                        onClick={() => onOpenChangeWrapped(false)}
                        data-testid="cancel-delete-button"
                        className="rounded-lg border border-[#E5E5E5] bg-white px-4 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={!ready || busy}
                        data-testid="confirm-delete-button"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#262626] disabled:cursor-not-allowed disabled:bg-[#A3A3A3]"
                    >
                        <Trash2 size={13} strokeWidth={2} />
                        {busy ? "Deleting…" : "Delete permanently"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
